import React, { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { CTA, FOOTER } from "@/constants/testIds";
import { toast } from "sonner";

export function FinalCTA({ onStart }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function joinWaitlist(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/waitlist", { email });
      toast.success("You're on the list. We'll be in touch soon.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      id="final-cta"
      data-testid={CTA.root}
      className="relative py-32 md:py-48 overflow-hidden bg-ivory"
    >
      <img
        aria-hidden
        src="https://images.unsplash.com/photo-1705837863332-7162639852d8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=60"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.14] mix-blend-multiply pointer-events-none"
        alt=""
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-end">
        <div className="col-span-12 lg:col-span-8">
          <span className="text-xs uppercase tracking-[0.3em] text-saffron">Begin</span>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] tracking-tighter text-ink leading-[0.9]"
          >
            Start preserving your
            <span className="italic text-tulsi"> family legacy </span>
            today.
          </motion.h2>
          <p className="mt-6 max-w-2xl text-ink-50 text-lg font-light leading-relaxed">
            Build your tree, uncover forgotten stories, reconnect with relatives and create a
            lasting archive for generations you'll never meet.
          </p>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <form onSubmit={joinWaitlist} className="border border-ivory-300 rounded-2xl p-6 bg-ivory">
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Join the archive</div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={CTA.waitlistInput}
                placeholder="you@family.in"
                className="flex-1 h-12 bg-transparent border-b border-ivory-300 focus:border-tulsi focus:outline-none px-1 font-serif text-lg text-ink placeholder:text-ink-50/50"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid={CTA.waitlistSubmit}
                className="h-12 px-4 rounded-full bg-tulsi text-ivory text-sm font-medium hover:bg-tulsi-deep disabled:opacity-60"
              >
                {busy ? "…" : "Join"}
              </button>
            </div>
            <button
              type="button"
              onClick={onStart}
              data-testid={CTA.ctaGetStarted}
              className="mt-4 w-full h-12 rounded-full bg-saffron text-ivory text-sm font-medium hover:bg-saffron-deep transition"
            >
              Get started free
            </button>
            <div className="mt-4 text-xs text-ink-50 leading-relaxed">
              Free forever for personal archives. Part of the SocialPulse network.
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export function SiteFooter() {
  return (
    <footer data-testid={FOOTER.root} className="bg-tulsi text-ivory relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 40 40" className="w-8 h-8 text-marigold" fill="none" stroke="currentColor" strokeWidth="1.4">
              <circle cx="20" cy="10" r="3.2" />
              <circle cx="10" cy="24" r="3.2" />
              <circle cx="30" cy="24" r="3.2" />
              <circle cx="20" cy="34" r="3" />
              <path d="M20 13v6M17 21 12 24M23 21l5 3M12 27l7 5M28 27l-7 5" />
            </svg>
            <div className="font-serif text-xl">IndianFamilyTree</div>
          </div>
          <p className="mt-6 text-ivory/60 text-sm font-light max-w-sm leading-relaxed">
            A digital heritage platform for the Indian diaspora — where family history meets
            geography, culture and story. Part of the SocialPulse network.
          </p>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ivory/50">Build</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="#tree-demo" className="ink-link">Family Tree</a></li>
            <li><a href="#featured" className="ink-link">Featured Trees</a></li>
            <li><a href="#memorials" className="ink-link">Memorials</a></li>
            <li><a href="#records" className="ink-link">Records</a></li>
          </ul>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ivory/50">Explore</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="#heritage" className="ink-link">States</a></li>
            <li><a href="#heritage" className="ink-link">Districts</a></li>
            <li><a href="#collections" className="ink-link">Collections</a></li>
            <li><a href="#collections" className="ink-link">Diaspora</a></li>
          </ul>
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.28em] text-ivory/50">Network</div>
          <div className="mt-4 font-serif text-xl">SocialPulse</div>
          <div className="mt-1 text-sm text-ivory/60">Places · Events · Culture · Community</div>
        </div>
      </div>
      <div className="border-t border-ivory/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4 text-xs text-ivory/50">
          <div>© {new Date().getFullYear()} IndianFamilyTree.com · a child site of SocialPulse</div>
          <div className="font-devanagari text-lg text-marigold">कुटुम्ब · Family · वंशम्</div>
        </div>
      </div>
    </footer>
  );
}
